import { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MessageSquare,
  Pencil,
  Star,
  Trash2,
} from "lucide-react";
import { toast } from "react-toastify";
import { useBrandAuthCheck } from "../../../hooks/useBrandAuthCheck";
import {
  createInfluencerReview,
  deleteInfluencerReview,
  getInfluencerReviews,
  updateInfluencerReview,
} from "../../services/marketplaceApi";
import {
  formatInfluencerRating,
  getEffectiveInfluencerRating,
} from "../../utils/rating";

const DEFAULT_FORM = {
  rating: 5,
  comment: "",
};

const RATING_LEVELS = [5, 4, 3, 2, 1];
const REVIEW_TITLE_BY_RATING = {
  5: "Excellent collaboration",
  4: "Strong collaboration",
  3: "Average experience",
  2: "Needs improvement",
  1: "Poor collaboration",
};
const AVATAR_STYLES = [
  "bg-[#1d4ed8] text-white",
  "bg-[#dc2626] text-white",
  "bg-[#7c3aed] text-white",
  "bg-[#0f766e] text-white",
  "bg-[#ea580c] text-white",
];

const createEmptyReviewsState = () => ({
  reviews: [],
  totalReviews: 0,
  averageRating: 0,
  ratingBreakdown: RATING_LEVELS.map((rating) => ({
    rating,
    count: 0,
  })),
  pagination: {
    page: 1,
    limit: 10,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  },
});

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const formatReviewDate = (value) => {
  if (!value) {
    return "";
  }

  return new Date(value).toLocaleDateString("en-GB");
};

const getCompanyInitials = (companyName = "") => {
  const parts = String(companyName)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  if (!parts.length) {
    return "R";
  }

  return parts.map((part) => part[0]?.toUpperCase() || "").join("");
};

const getAvatarStyle = (companyName = "") => {
  const seed = String(companyName)
    .split("")
    .reduce((sum, char) => sum + char.charCodeAt(0), 0);

  return AVATAR_STYLES[seed % AVATAR_STYLES.length];
};

const getReviewCopy = (review) => {
  const comment = String(review?.comment || "").trim();

  if (!comment) {
    return {
      title: "No written review provided",
      body: "This company left a rating without additional feedback.",
    };
  }

  const paragraphs = comment
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (paragraphs.length > 1) {
    return {
      title: paragraphs[0],
      body: paragraphs.slice(1).join(" "),
    };
  }

  const sentences = comment.split(/(?<=[.!?])\s+/).filter(Boolean);

  if (sentences.length > 1) {
    return {
      title: sentences[0].replace(/[.!?]+$/, ""),
      body: sentences.slice(1).join(" "),
    };
  }

  return {
    title: REVIEW_TITLE_BY_RATING[review?.rating] || "Review",
    body: comment,
  };
};

const StarRating = ({ value, size = 18, className = "" }) => {
  const percentage = `${clamp((Number(value) / 5) * 100, 0, 100)}%`;
  const stars = Array.from({ length: 5 }, (_, index) => (
    <Star
      key={index}
      size={size}
      className="shrink-0"
      fill="currentColor"
      strokeWidth={1.7}
    />
  ));

  return (
    <div className={`relative inline-flex ${className}`}>
      <div className="flex items-center gap-1 text-gray-200">{stars}</div>
      <div className="absolute inset-0 overflow-hidden" style={{ width: percentage }}>
        <div className="flex items-center gap-1 text-[#f59e0b]">{stars}</div>
      </div>
    </div>
  );
};

const Reviews = ({ influencer, onSummaryChange }) => {
  const { isBrand, isAuthenticated } = useBrandAuthCheck();
  const [reviewsData, setReviewsData] = useState(createEmptyReviewsState);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [page, setPage] = useState(1);
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [showMethodology, setShowMethodology] = useState(false);
  const composerRef = useRef(null);

  const username = influencer?.username;
  const influencerId = influencer?.id || influencer?._id;
  const reviews = reviewsData.reviews || [];

  const ownReview = useMemo(
    () => reviews.find((review) => review.canEdit),
    [reviews]
  );

  const canSubmitReview =
    Boolean(influencerId) && Boolean(username) && isBrand && isAuthenticated;

  useEffect(() => {
    setPage(1);
    setIsComposerOpen(false);
  }, [username]);

  useEffect(() => {
    if (!username || !isBrand || !isAuthenticated) {
      setReviewsData(createEmptyReviewsState());
      setError("");
      return;
    }

    let cancelled = false;

    const fetchReviews = async () => {
      try {
        setLoading(true);
        setError("");

        const data = await getInfluencerReviews(username, { page, limit: 10 });

        if (!cancelled) {
          setReviewsData(data);
          const effectiveAverageRating = getEffectiveInfluencerRating({
            rating: data.averageRating,
            reviewCount: data.totalReviews,
            isVerified: influencer?.isVerified,
          });
          onSummaryChange?.({
            averageRating: effectiveAverageRating ?? 0,
            totalReviews: data.totalReviews || 0,
          });
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(
            fetchError?.response?.data?.message ||
              fetchError?.message ||
              "Failed to load reviews"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReviews();

    return () => {
      cancelled = true;
    };
  }, [username, page, isBrand, isAuthenticated, onSummaryChange, influencer?.isVerified]);

  useEffect(() => {
    if (ownReview) {
      setForm({
        rating: ownReview.rating || 5,
        comment: ownReview.comment || "",
      });
      return;
    }

    setForm(DEFAULT_FORM);
  }, [ownReview]);

  const scrollComposerIntoView = () => {
    requestAnimationFrame(() => {
      composerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  };

  const openComposer = () => {
    if (!canSubmitReview) {
      toast.info("Sign in with a brand account to write a review.");
      return;
    }

    setIsComposerOpen(true);
    scrollComposerIntoView();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!canSubmitReview) {
      toast.error("Only authenticated brand accounts can submit reviews.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        influencerId,
        influencerUsername: username,
        rating: Number(form.rating),
        comment: form.comment,
      };

      const isEditing = Boolean(ownReview?.id);

      if (isEditing) {
        await updateInfluencerReview(ownReview.id, {
          rating: payload.rating,
          comment: payload.comment,
        });
        toast.success("Review updated successfully.");
      } else {
        await createInfluencerReview(payload);
        toast.success("Review added successfully.");
      }

      const targetPage = isEditing ? page : 1;
      const refreshed = await getInfluencerReviews(username, {
        page: targetPage,
        limit: 10,
      });
      if (!isEditing) {
        setPage(1);
      }
      setReviewsData(refreshed);
      const effectiveAverageRating = getEffectiveInfluencerRating({
        rating: refreshed.averageRating,
        reviewCount: refreshed.totalReviews,
        isVerified: influencer?.isVerified,
      });
      onSummaryChange?.({
        averageRating: effectiveAverageRating ?? 0,
        totalReviews: refreshed.totalReviews || 0,
      });
      setIsComposerOpen(false);
    } catch (submitError) {
      toast.error(
        submitError?.response?.data?.message ||
          submitError?.message ||
          "Failed to save review"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!ownReview?.id) {
      return;
    }

    try {
      setSubmitting(true);
      await deleteInfluencerReview(ownReview.id);
      toast.success("Review deleted successfully.");

      const refreshed = await getInfluencerReviews(username, { page: 1, limit: 10 });
      setPage(1);
      setReviewsData(refreshed);
      const effectiveAverageRating = getEffectiveInfluencerRating({
        rating: refreshed.averageRating,
        reviewCount: refreshed.totalReviews,
        isVerified: influencer?.isVerified,
      });
      onSummaryChange?.({
        averageRating: effectiveAverageRating ?? 0,
        totalReviews: refreshed.totalReviews || 0,
      });
      setIsComposerOpen(false);
    } catch (deleteError) {
      toast.error(
        deleteError?.response?.data?.message ||
          deleteError?.message ||
          "Failed to delete review"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const hasReviews = reviews.length > 0;
  const averageRatingValue = getEffectiveInfluencerRating({
    rating: reviewsData.averageRating,
    reviewCount: reviewsData.totalReviews,
    isVerified: influencer?.isVerified,
  });
  const averageRating = formatInfluencerRating(averageRatingValue, { fallback: "0.0" });
  const ratingBreakdown = useMemo(() => {
    if (Array.isArray(reviewsData.ratingBreakdown) && reviewsData.ratingBreakdown.length) {
      return reviewsData.ratingBreakdown;
    }

    const fallbackBreakdown = reviews.reduce((accumulator, review) => {
      const rating = Number(review.rating);
      if (rating >= 1 && rating <= 5) {
        accumulator[rating] += 1;
      }
      return accumulator;
    }, { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 });

    return RATING_LEVELS.map((rating) => ({
      rating,
      count: fallbackBreakdown[rating] || 0,
    }));
  }, [reviews, reviewsData.ratingBreakdown]);
  const emptyMessage =
    !isBrand || !isAuthenticated
      ? "Sign in with a brand account to view and write reviews."
      : "No reviews yet for this creator.";

  return (
    <section className="rounded-[28px] border border-[#dddddd] bg-white shadow-[0_12px_40px_rgba(15,23,42,0.04)]">
      <div className="grid lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="border-b border-[#e7e7e7] bg-[#fbfbfb] p-6 sm:p-8 lg:border-b-0 lg:border-r">
          <div>
            <h2 className="text-[30px] font-semibold tracking-[-0.03em] text-[#111111]">
              Company reviews
            </h2>
            <div className="mt-3 flex items-center gap-3">
              <StarRating value={averageRatingValue} size={19} />
              <span className="text-[17px] font-medium text-[#1f2937]">
                {averageRating} out of 5
              </span>
            </div>
            <p className="mt-3 text-[15px] text-[#6b7280]">
              {reviewsData.totalReviews} company reviews
            </p>
          </div>

          <div className="mt-7 space-y-4">
            {ratingBreakdown.map((item) => {
              const percentage =
                reviewsData.totalReviews > 0
                  ? Math.round((item.count / reviewsData.totalReviews) * 100)
                  : 0;

              return (
                <div key={item.rating} className="grid grid-cols-[54px_minmax(0,1fr)_42px] items-center gap-3">
                  <span className="text-[15px] text-[#374151]">{item.rating} star</span>
                  <div className="h-3 overflow-hidden rounded-full bg-[#ececec]">
                    <div
                      className="h-full rounded-full bg-[#ff6a00] transition-all"
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                  <span className="text-right text-[15px] text-[#4b5563]">{percentage}%</span>
                </div>
              );
            })}
          </div>

          <div className="mt-7 border-t border-[#ececec] pt-6">
            <button
              type="button"
              onClick={() => setShowMethodology((current) => !current)}
              className="flex w-full items-center justify-between gap-3 text-left text-[16px] font-medium text-[#1f2937]"
            >
              <span>How are ratings calculated?</span>
              {showMethodology ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>

            {showMethodology && (
              <p className="mt-3 text-[14px] leading-6 text-[#6b7280]">
                Ratings are based on verified company reviews. The bar chart shows the
                percentage of total reviews that fall into each star bucket.
              </p>
            )}
          </div>

          <div className="mt-7 border-t border-[#ececec] pt-6">
            <h3 className="text-[19px] font-semibold text-[#111111]">
              Review this influencer
            </h3>
            <p className="mt-2 text-[15px] text-[#6b7280]">
              Share your experience with this creator
            </p>

            <button
              type="button"
              onClick={openComposer}
              disabled={!canSubmitReview}
              className="mt-5 rounded-full bg-[#111111] px-4 py-2 md:px-5 md:py-3 text-sm md:text-[15px] font-medium text-white transition hover:bg-[#222222] disabled:cursor-not-allowed disabled:bg-[#d1d5db] disabled:text-[#6b7280]"
            >
              {ownReview ? "Edit your review" : "Write a review"}
            </button>

            {!canSubmitReview && (
              <p className="mt-3 text-xs md:text-[13px] leading-4 md:leading-5 text-[#6b7280]">
                Only authenticated brand accounts can submit reviews.
              </p>
            )}
          </div>
        </aside>

        <div className="p-4 md:p-6 sm:p-8">
          <div className="border-b border-[#ececec] pb-5">
            <div className="flex flex-wrap items-center gap-3 text-[#111111]">
              <h3 className="md:text-2xl text-xl font-semibold tracking-[-0.03em]">
                {reviewsData.totalReviews} Reviews
              </h3>
              <span className="text-[#9ca3af]">·</span>
              <div className="flex items-center gap-2 text-sm md:text-[17px] font-semibold">
                <span className="text-[#f59e0b]">★</span>
                <span>{averageRating}</span>
              </div>
            </div>
          </div>

          {loading && (
            <div className="py-12 text-center text-xs md:text-sm text-gray-500">
              Loading reviews...
            </div>
          )}

          {!loading && error && (
            <div className="py-12 text-center text-xs md:text-sm text-red-600">{error}</div>
          )}

          {!loading && !error && !hasReviews && (
            <div className="py-14 text-center">
              <MessageSquare size={40} className="mx-auto mb-4 text-[#d1d5db]" />
              <p className="text-xs md:text-[15px] text-[#6b7280]">{emptyMessage}</p>
            </div>
          )}

          {!loading &&
            !error &&
            hasReviews &&
            reviews.map((review) => {
              const reviewCopy = getReviewCopy(review);

              return (
                <article
                  key={review.id}
                  className="border-b border-[#ececec] py-6 last:border-b-0"
                >
                  <div className="flex items-center gap-4">
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm md:h-12 md:w-12 md:text-[18px] font-semibold ${getAvatarStyle(
                        review.companyName
                      )}`}
                    >
                      {getCompanyInitials(review.companyName)}
                    </div>

                    <div className="flex min-w-0 flex-1 flex-col gap-2 md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-wrap items-center gap-3">
                        <p className="text-sm md:text-[18px] font-semibold text-[#111111]">
                          {review.companyName}
                        </p>
                        <span className="text-xs md:text-[14px] text-[#6b7280]">
                          {formatReviewDate(review.createdAt)}
                        </span>
                        {review.canEdit && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f4f6] px-3 py-1 text-[12px] font-medium text-[#4b5563]">
                            <Pencil size={12} />
                            Your review
                          </span>
                        )}
                      </div>

                      {review.canEdit && (
                        <div className="flex items-center gap-3 text-sm">
                          <button
                            type="button"
                            onClick={() => {
                              setIsComposerOpen(true);
                              scrollComposerIntoView();
                            }}
                            disabled={submitting}
                            className="font-medium text-[#111111] transition hover:text-[#ff6a00] disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="inline-flex items-center gap-1 font-medium text-[#dc2626] transition hover:text-[#b91c1c] disabled:opacity-50"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <StarRating value={review.rating} size={16} />
                    <span className="inline-flex items-center gap-2 rounded-md px-2 py-1 md:px-3 md:py-1.5 md:text-[13px] text-xs font-medium text-[#C45500]">
                      Verified Collaboration
                    </span>
                  </div>

                  <h4 className="mt-2 text-sm md:text-[18px] font-semibold leading-4 md:leading-6 text-[#111111]">
                    {reviewCopy.title}
                  </h4>
                  <p className="mt-1 text-xs md:text-[15px] leading-4 md:leading-6 text-[#374151]">
                    {reviewCopy.body}
                  </p>
                </article>
              );
            })}

          {!loading &&
            !error &&
            reviewsData.pagination &&
            reviewsData.pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between gap-4 border-t border-[#ececec] pt-5">
                <button
                  type="button"
                  onClick={() =>
                    setPage((current) => Math.max(1, current - 1))
                  }
                  disabled={!reviewsData.pagination.hasPrevPage}
                  className="rounded-full border border-[#d1d5db] px-4 py-2 text-sm font-medium text-[#374151] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>

                <span className="text-sm text-[#6b7280]">
                  Page {reviewsData.pagination.page} of{" "}
                  {reviewsData.pagination.totalPages}
                </span>

                <button
                  type="button"
                  onClick={() => setPage((current) => current + 1)}
                  disabled={!reviewsData.pagination.hasNextPage}
                  className="rounded-full border border-[#d1d5db] px-4 py-2 text-sm font-medium text-[#374151] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}

          <div
            ref={composerRef}
            className="mt-6 rounded-[24px] border border-[#ececec] bg-[#fafafa] p-5 sm:p-6"
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-sm md:text-[18px] font-semibold text-[#111111]">
                  {ownReview ? "Update your review" : "Write a review"}
                </h3>
                <p className="mt-1 text-xs md:text-[15px] text-[#6b7280]">
                  Share your experience working with this creator.
                </p>
              </div>

              {!isComposerOpen && canSubmitReview && (
                <button
                  type="button"
                  onClick={openComposer}
                  className="rounded-full border border-[#d1d5db] bg-white px-5 py-3 text-[15px] font-medium text-[#111111] transition hover:border-[#111111]"
                >
                  {ownReview ? "Edit review" : "Write a review"}
                </button>
              )}
            </div>

            {!canSubmitReview && (
              <p className="mt-4 text-xs md:text-[14px] leading-4 md:leading-6 text-[#6b7280]">
                Sign in with a brand account to create, edit, or delete your review.
              </p>
            )}

            {canSubmitReview && isComposerOpen && (
              <form onSubmit={handleSubmit} className="mt-5 space-y-5">
                <div>
                  <label className="mb-3 block text-xs md:text-sm font-medium text-[#374151]">
                    Rating
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {RATING_LEVELS.slice().reverse().map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() =>
                          setForm((current) => ({
                            ...current,
                            rating: value,
                          }))
                        }
                        className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                          Number(form.rating) === value
                            ? "border-[#f59e0b] bg-[#fff7da] text-[#8a6d00]"
                            : "border-[#d1d5db] bg-white text-[#4b5563] hover:border-[#f59e0b]"
                        }`}
                      >
                        <Star size={16} fill="currentColor" />
                        {value}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label
                    htmlFor="review-comment"
                    className="mb-3 block text-xs md:text-sm font-medium text-[#374151]"
                  >
                    Review
                  </label>
                  <textarea
                    id="review-comment"
                    value={form.comment}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        comment: event.target.value.slice(0, 500),
                      }))
                    }
                    rows={5}
                    maxLength={500}
                    placeholder="Tell others how the collaboration went."
                    className="w-full rounded-[20px] border border-[#d1d5db] bg-white px-4 py-3 text-sm text-[#374151] outline-none transition focus:border-[#ff6a00]"
                  />
                  <p className="mt-2 text-xs md:text-[14px] text-[#6b7280]">
                    {form.comment.length}/500 characters
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="rounded-full bg-[#111111] px-5 py-3 text-sm font-medium text-white transition hover:bg-[#222222] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {submitting
                      ? ownReview
                        ? "Updating..."
                        : "Submitting..."
                      : ownReview
                      ? "Update review"
                      : "Submit review"}
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsComposerOpen(false)}
                    disabled={submitting}
                    className="rounded-full border border-[#d1d5db] bg-white px-5 py-3 text-sm font-medium text-[#374151] transition hover:border-[#111111] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
